import React, { useState } from 'react';
import {
    Container,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Box,
    CircularProgress,
    Divider,
    Link,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    makeStyles
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Mail, Lock, User, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useHistory, useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
    },
    card: {
        maxWidth: 400,
        width: '100%',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    },
    header: {
        textAlign: 'center',
        marginBottom: theme.spacing(3),
    },
    title: {
        fontWeight: 700,
        color: '#333',
        marginBottom: theme.spacing(1),
    },
    subtitle: {
        color: '#666',
        fontSize: '0.9rem',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    input: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
        },
    },
    signupButton: {
        padding: theme.spacing(1.5),
        borderRadius: 12,
        background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
        color: 'white',
        fontWeight: 600,
        '&:hover': {
            background: 'linear-gradient(45deg, #667eea 60%, #764ba2 100%)',
        },
        '&:disabled': {
            background: '#ccc',
        },
    },
    googleButton: {
        padding: theme.spacing(1.5),
        borderRadius: 12,
        border: '2px solid #ddd',
        color: '#333',
        fontWeight: 600,
        '&:hover': {
            borderColor: '#bbb',
            backgroundColor: '#f5f5f5',
        },
    },
    divider: {
        margin: theme.spacing(2, 0),
        color: '#666',
        textAlign: 'center',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: '#ddd',
            zIndex: 0
        },
        '& .MuiTypography-root': {
            backgroundColor: 'white',
            padding: theme.spacing(0, 2),
            position: 'relative',
            zIndex: 1
        }
    },
    footer: {
        textAlign: 'center',
        marginTop: theme.spacing(3),
        color: '#666',
    },
    link: {
        color: '#667eea',
        cursor: 'pointer',
        textDecoration: 'none',
        fontWeight: 600,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    bonusText: {
        backgroundColor: '#e8f5e8',
        color: '#2e7d32',
        padding: theme.spacing(1),
        borderRadius: 8,
        fontSize: '0.85rem',
        textAlign: 'center',
        marginBottom: theme.spacing(2),
    },
}));

const Signup = () => {
    const classes = useStyles();
    const { signup, loginWithGoogle } = useAuth();
    const history = useHistory();
    const location = useLocation();
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        gender: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { from } = location.state || { from: { pathname: '/guide' } };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
    };

    const validateForm = () => {
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return false;
        }
        if (!formData.name.trim()) {
            setError('Please enter your name');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        setError('');

        try {
            await signup(formData.email, formData.password, formData.name.trim(), formData.gender);
            history.replace(from);
        } catch (error) {
            setError(getErrorMessage(error.code));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignup = async () => {
        setLoading(true);
        setError('');

        try {
            await loginWithGoogle();
            history.replace(from);
        } catch (error) {
            setError('Failed to sign up with Google');
        } finally {
            setLoading(false);
        }
    };

    const getErrorMessage = (errorCode) => {
        switch (errorCode) {
            case 'auth/email-already-in-use':
                return 'An account with this email already exists.';
            case 'auth/invalid-email':
                return 'Please enter a valid email address.';
            case 'auth/weak-password':
                return 'Password should be at least 6 characters.';
            default:
                return 'An error occurred. Please try again.';
        }
    };

    const goToLogin = () => {
        history.push('/login', { from });
    };

    return (
        <Box className={classes.root}>
            <Container maxWidth="sm">
                <Card className={classes.card}>
                    <CardContent style={{ padding: '2rem' }}>
                        <Box className={classes.header}>
                            <Typography variant="h4" className={classes.title}>
                                Create Account
                            </Typography>
                            <Typography variant="body2" className={classes.subtitle}>
                                Join InstaWorthy and start creating AI photos
                            </Typography>
                        </Box>

                        <Box className={classes.bonusText}>
                            🎉 Get 10 free tokens to start generating photos!
                        </Box>

                        {error && (
                            <Alert severity="error" style={{ marginBottom: '1rem' }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} className={classes.form}>
                            <TextField
                                name="name"
                                type="text"
                                label="Full Name"
                                variant="outlined"
                                fullWidth
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className={classes.input}
                                InputProps={{
                                    startAdornment: <User size={20} style={{ marginRight: 8, color: '#666' }} />
                                }}
                            />

                            <TextField
                                name="email"
                                type="email"
                                label="Email Address"
                                variant="outlined"
                                fullWidth
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className={classes.input}
                                InputProps={{
                                    startAdornment: <Mail size={20} style={{ marginRight: 8, color: '#666' }} />
                                }}
                            />

                            <TextField
                                name="password"
                                type="password"
                                label="Password"
                                variant="outlined"
                                fullWidth
                                required
                                value={formData.password}
                                onChange={handleChange}
                                className={classes.input}
                                InputProps={{
                                    startAdornment: <Lock size={20} style={{ marginRight: 8, color: '#666' }} />
                                }}
                                helperText="Minimum 6 characters"
                            />

                            <TextField
                                name="confirmPassword"
                                type="password"
                                label="Confirm Password"
                                variant="outlined"
                                fullWidth
                                required
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className={classes.input}
                                InputProps={{
                                    startAdornment: <Lock size={20} style={{ marginRight: 8, color: '#666' }} />
                                }}
                            />

                            <FormControl variant="outlined" fullWidth className={classes.input}>
                                <InputLabel>Gender (Optional)</InputLabel>
                                <Select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleChange}
                                    label="Gender (Optional)"
                                >
                                    <MenuItem value="">
                                        <em>Prefer not to say</em>
                                    </MenuItem>
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                className={classes.signupButton}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <UserPlus size={20} />}
                            >
                                {loading ? 'Creating Account...' : 'Create Account'}
                            </Button>
                        </form>

                        <Box className={classes.divider}>
                            <Typography variant="body2">or</Typography>
                        </Box>

                        <Button
                            variant="outlined"
                            fullWidth
                            className={classes.googleButton}
                            onClick={handleGoogleSignup}
                            disabled={loading}
                        >
                            <img 
                                src="https://developers.google.com/identity/images/g-logo.png" 
                                alt="Google" 
                                style={{ width: 20, height: 20, marginRight: 8 }}
                            />
                            Continue with Google
                        </Button>

                        <Box className={classes.footer}>
                            <Typography variant="body2">
                                Already have an account?{' '}
                                <Link className={classes.link} onClick={goToLogin}>
                                    Sign in here
                                </Link>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default Signup;